// Enhanced types.ts - Updated to comply with TSL v1.0 specification
// Updated: Unified CUSTOM block, enhanced types, TSL-compliant structure

export enum BlockType {
  // Strategy Foundation (2 blocks)
  STRATEGY_CONFIG = 'strategy_config',
  PRICE_DATA = 'price_data',

  // Technical Indicators (5 blocks)
  MOVING_AVERAGE = 'moving_average',
  MOMENTUM_OSCILLATOR = 'momentum_oscillator',
  VOLATILITY = 'volatility',
  VOLUME = 'volume',
  TREND = 'trend',

  // Logic & Conditions (2 blocks)
  EXPRESSION = 'expression',
  LOGIC_GATE = 'logic_gate',

  // Pattern Recognition (2 blocks) - Updated per TSL spec
  CHART_PATTERNS = 'chart_patterns',
  CANDLESTICK_PATTERNS = 'candlestick_patterns',

  // Market Filters (1 block)
  TIME_FILTER = 'time_filter',

  // Trading Execution (4 blocks) - Terminal blocks per TSL spec
  ENTRY = 'entry',
  EXIT = 'exit',
  STOP_LOSS = 'stop_loss',
  TAKE_PROFIT = 'take_profit',

  // Custom Block (1 unified block) - NEW per TSL spec
  CUSTOM = 'custom',

  // P9.1 Stateful setup language (2 blocks): structural-event detector + setup state machine.
  // setup_event: an objective, versioned market-structure event (sweep/FVG/displacement/…)
  //   with an explicit confirmedAt (no-lookahead) contract — src/engine/setup/structuralPrimitives.ts.
  // setup_state: the deterministic scenario state machine (THEN/WITHIN/NOT_WITHIN/expiry/
  //   cooldown) whose transitions are serialized + auditable — src/engine/setup/setupStateMachine.ts.
  SETUP_EVENT = 'setup_event',
  SETUP_STATE = 'setup_state',
}

export const BLOCK_COLORS: Record<BlockType, string> = {
  // Strategy Foundation
  [BlockType.STRATEGY_CONFIG]: '#06B6D4', // Cyan
  [BlockType.PRICE_DATA]: '#6366F1', // Indigo

  // Technical Indicators
  [BlockType.MOVING_AVERAGE]: '#6366F1', // Indigo
  [BlockType.MOMENTUM_OSCILLATOR]: '#8B5CF6', // Purple
  [BlockType.VOLATILITY]: '#F97316', // Orange
  [BlockType.VOLUME]: '#10B981', // Green
  [BlockType.TREND]: '#14B8A6', // Teal

  // Logic & Conditions
  [BlockType.EXPRESSION]: '#14B8A6', // Teal
  [BlockType.LOGIC_GATE]: '#6366F1', // Indigo

  // Pattern Recognition (2 separate blocks)
  [BlockType.CHART_PATTERNS]: '#F43F5E', // Rose
  [BlockType.CANDLESTICK_PATTERNS]: '#EC4899', // Pink

  // Market Filters
  [BlockType.TIME_FILTER]: '#EAB308', // Yellow

  // Trading Execution (Terminal blocks)
  [BlockType.ENTRY]: '#22C55E', // Green
  [BlockType.EXIT]: '#F97316', // Orange
  [BlockType.STOP_LOSS]: '#EF4444', // Red
  [BlockType.TAKE_PROFIT]: '#3B82F6', // Blue

  // Custom Block (unified)
  [BlockType.CUSTOM]: '#7C3AED', // Violet

  // P9.1 Stateful setup language
  [BlockType.SETUP_EVENT]: '#0EA5E9', // Sky
  [BlockType.SETUP_STATE]: '#A855F7', // Fuchsia
};

// TSL-compliant connection rules - Updated per TSL spec
export const TSL_CONNECTION_RULES: Record<BlockType, BlockType[]> = {
  // STRATEGY_CONFIG is NOT CONNECTABLE - standalone settings only
  [BlockType.STRATEGY_CONFIG]: [], // No connections allowed

  // PRICE_DATA can connect to indicators, expressions, logic gates, and patterns
  [BlockType.PRICE_DATA]: [
    BlockType.MOVING_AVERAGE,
    BlockType.MOMENTUM_OSCILLATOR,
    BlockType.VOLATILITY,
    BlockType.VOLUME,
    BlockType.TREND,
    BlockType.CUSTOM,
    BlockType.EXPRESSION,
    BlockType.LOGIC_GATE,
    BlockType.CHART_PATTERNS,
    BlockType.CANDLESTICK_PATTERNS,
  ],

  // Technical Indicators - Must flow through Expression or Logic Gate blocks
  [BlockType.MOVING_AVERAGE]: [BlockType.EXPRESSION, BlockType.LOGIC_GATE],
  [BlockType.MOMENTUM_OSCILLATOR]: [BlockType.EXPRESSION, BlockType.LOGIC_GATE],
  [BlockType.VOLATILITY]: [BlockType.EXPRESSION, BlockType.LOGIC_GATE],
  [BlockType.VOLUME]: [BlockType.EXPRESSION, BlockType.LOGIC_GATE],
  [BlockType.TREND]: [BlockType.EXPRESSION, BlockType.LOGIC_GATE],

  // Logic blocks can interconnect and connect to terminal blocks
  [BlockType.EXPRESSION]: [
    BlockType.LOGIC_GATE,
    BlockType.EXPRESSION,
    BlockType.ENTRY,
    BlockType.EXIT,
    BlockType.STOP_LOSS,
    BlockType.TAKE_PROFIT,
  ],
  [BlockType.LOGIC_GATE]: [
    BlockType.EXPRESSION,
    BlockType.LOGIC_GATE,
    BlockType.ENTRY,
    BlockType.EXIT,
    BlockType.STOP_LOSS,
    BlockType.TAKE_PROFIT,
  ],

  // Pattern Recognition - Must flow through Expression or Logic Gate blocks
  [BlockType.CHART_PATTERNS]: [BlockType.EXPRESSION, BlockType.LOGIC_GATE],
  [BlockType.CANDLESTICK_PATTERNS]: [BlockType.EXPRESSION, BlockType.LOGIC_GATE],

  // Market Filters - Must flow through Expression or Logic Gate blocks
  [BlockType.TIME_FILTER]: [BlockType.EXPRESSION, BlockType.LOGIC_GATE],

  // Custom Block - Must flow through Expression or Logic Gate blocks
  [BlockType.CUSTOM]: [BlockType.EXPRESSION, BlockType.LOGIC_GATE],

  // P9.1 Stateful setup language: events feed the state machine (and expressions);
  // the state machine's arming output flows through expression/logic gates like any signal.
  [BlockType.SETUP_EVENT]: [BlockType.SETUP_STATE, BlockType.EXPRESSION, BlockType.LOGIC_GATE],
  [BlockType.SETUP_STATE]: [BlockType.EXPRESSION, BlockType.LOGIC_GATE],

  // Terminal blocks (no outputs) - TSL compliance
  [BlockType.ENTRY]: [],
  [BlockType.EXIT]: [],
  [BlockType.STOP_LOSS]: [],
  [BlockType.TAKE_PROFIT]: [],
};

// Helper function to check if a block is terminal
export const isTerminalBlock = (blockType: BlockType): boolean => {
  return [
    BlockType.ENTRY,
    BlockType.EXIT,
    BlockType.STOP_LOSS,
    BlockType.TAKE_PROFIT,
  ].includes(blockType);
};

// Helper function to check if a block requires structured flow
export const requiresStructuredFlow = (blockType: BlockType): boolean => {
  return [
    BlockType.MOVING_AVERAGE,
    BlockType.MOMENTUM_OSCILLATOR,
    BlockType.VOLATILITY,
    BlockType.VOLUME,
    BlockType.TREND,
    BlockType.CHART_PATTERNS,
    BlockType.CANDLESTICK_PATTERNS,
    BlockType.TIME_FILTER,
    BlockType.CUSTOM,
    BlockType.SETUP_EVENT,
    BlockType.SETUP_STATE,
  ].includes(blockType);
};

// Rest of existing types...
export interface Point {
  x: number | string | Date;
  y: number;
}

export interface ChartPoint {
  x: number;
  y: number;
  label?: string;
  metadata?: Record<string, any>;
}

export interface Price {
  value: number;
  currency: string;
  precision: number;
}

export interface Volume {
  value: number;
  unit: 'shares' | 'lots' | 'contracts' | 'units';
}

export interface Duration {
  value: number;
  unit: 'seconds' | 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'years';
}

export interface Candle {
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  timestamp: Date;
  tickVolume?: number;
  spread?: number;
}

export interface Signal {
  type: 'entry' | 'exit' | 'filter' | 'alert';
  direction: 'long' | 'short' | 'close';
  strength: number; // 0-100
  timestamp: Date;
  price?: number;
  metadata?: Record<string, unknown>;
  source: string; // Block ID that generated the signal
}

// TSL Pattern Detection interface
export interface PatternDetection {
  index: number;
  pattern: string;
  confidence: number; // 0-1
  direction: 'bullish' | 'bearish' | 'neutral';
  data?: Record<string, unknown>;
}

export interface Position {
  id: string;
  symbol: string;
  side: 'long' | 'short';
  size: number;
  entryPrice: number;
  currentPrice: number;
  unrealizedPnL: number;
  realizedPnL: number;
  timestamp: Date;
  stopLoss?: number;
  takeProfit?: number;
  commission: number;
  swap?: number;
}

// Enhanced Order interface per TSL spec
export interface Order {
  id: string;
  symbol: string;
  type: 'market' | 'limit' | 'stop' | 'stop_limit' | 'trailing_stop' | 'one_cancels_other' | 'bracket';
  side: 'buy' | 'sell';
  quantity: number;
  price?: number;
  stopPrice?: number;
  timeInForce: 'GTC' | 'IOC' | 'FOK' | 'DAY' | 'GTD';
  status: 'pending' | 'filled' | 'partial' | 'cancelled' | 'rejected';
  timestamp: Date;
  expiration?: Date;
  parentOrderId?: string; // For bracket and OCO orders
  childOrderIds?: string[]; // For bracket and OCO orders
  fillPrice?: number;
  fillQuantity?: number;
  commission?: number;
}

export interface RuleViolation {
  rule: string;
  message: string;
  severity: 'warning' | 'error';
  value?: number;
  threshold?: number;
  timestamp?: Date;
}

export interface BacktestResult {
  strategyId: string;
  pnl: number;
  winRate: number;
  drawdown: number;
  expectancy: number;
  trades: Trade[];
  equityCurve: Point[];
  passesPropRules: boolean;
  ruleViolations: RuleViolation[];
}

export enum TradingPlatform {
  MT4 = 'mt4',
  MT5 = 'mt5',
  TRADINGVIEW = 'tradingview',
  NINJATRADER = 'ninjatrader',
  THINKORSWIM = 'thinkorswim',
  INTERACTIVE_BROKERS = 'interactive_brokers',
  BINANCE = 'binance',
  ALPACA = 'alpaca',
  OANDA = 'oanda',
  FXCM = 'fxcm',
}

export enum AssetClass {
  FOREX = 'forex',
  STOCKS = 'stocks',
  CRYPTO = 'crypto',
  COMMODITIES = 'commodities',
  FUTURES = 'futures',
  OPTIONS = 'options',
  BONDS = 'bonds',
  ETFS = 'etfs',
  INDICES = 'indices',
}

export enum TimeFrame {
  M1 = 'M1', // 1 minute
  M5 = 'M5', // 5 minutes
  M15 = 'M15', // 15 minutes
  M30 = 'M30', // 30 minutes
  H1 = 'H1', // 1 hour
  H4 = 'H4', // 4 hours
  D1 = 'D1', // 1 day
  W1 = 'W1', // 1 week
  MN1 = 'MN1', // 1 month
}

// Updated QuantityType per TSL spec
export enum QuantityType {
  FIXED = 'fixed',
  PERCENT_EQUITY = 'percent_equity',
  RISK_BASED = 'risk_based',
  CONTRACTS = 'contracts',
  CURRENCY = 'currency',
  KELLY_CRITERION = 'kelly_criterion',
  OPTIMAL_F = 'optimal_f',
  VOLATILITY_ADJUSTED = 'volatility_adjusted',
}

// Updated CommissionType per TSL spec
export enum CommissionType {
  FIXED = 'fixed',
  PER_SHARE = 'per_share',
  PERCENTAGE = 'percentage',
  PER_CONTRACT = 'per_contract',
  TIERED = 'tiered',
}

export enum ExitType {
  FULL = 'full',
  PARTIAL = 'partial',
  SCALED = 'scaled',
}

// Updated StopType per TSL spec
export enum StopType {
  FIXED_PRICE = 'fixed_price',
  FIXED_POINTS = 'fixed_points',
  PERCENTAGE = 'percentage',
  ATR_BASED = 'atr_based',
  TRAILING = 'trailing',
  TRAILING_LIMIT = 'trailing_limit',
  TIME_BASED = 'time_based',
  VOLATILITY_ADJUSTED = 'volatility_adjusted',
  BREAKEVEN = 'breakeven',
}

// Updated TakeProfitType per TSL spec
export enum TakeProfitType {
  FIXED_PRICE = 'fixed_price',
  FIXED_POINTS = 'fixed_points',
  PERCENTAGE = 'percentage',
  RISK_REWARD = 'risk_reward',
  ATR_BASED = 'atr_based',
  TRAILING = 'trailing',
  SCALED = 'scaled',
}

export enum ExpressionOperator {
  GREATER_THAN = '>',
  LESS_THAN = '<',
  GREATER_THAN_OR_EQUAL = '>=',
  LESS_THAN_OR_EQUAL = '<=',
  EQUAL = '==',
  NOT_EQUAL = '!=',
  CROSSES_ABOVE = 'crosses_above',
  CROSSES_BELOW = 'crosses_below',
  INSIDE_RANGE = 'inside_range',
  OUTSIDE_RANGE = 'outside_range',
  RISING = 'rising',
  FALLING = 'falling',
}

export enum ExpressionSourceType {
  INPUT = 'input', // Connected block signal
  STATIC = 'static', // Static value
  PRICE = 'price', // Global price data
  PRICE_DATA = 'price_data', // Connected price data block
  FEATURE = 'feature', // Named external feature-series operand (enriched features / fundamentals via featureSeries)
  // TSL v1.1.1 A.7: structured range operand for inside_range / outside_range.
  // The Expression block's left/right scalar comparison can't express a
  // {low, high} band — these two source types pair with `rightRange` (static
  // boundaries) or `rightLowInputBlockId` + `rightHighInputBlockId` (dynamic
  // boundaries) instead of the scalar `rightValue`/`rightInputBlockId`.
  RANGE_STATIC = 'range_static',
  RANGE_INPUT = 'range_input',
}

export interface ExpressionPersistence {
  required: number;
  type: 'consecutive' | 'within_period';
  period?: number;
}

// Enhanced Block interface per TSL spec
export interface Block {
  id: string;
  type: BlockType;
  position: { x: number; y: number };
  data: BlockData;
  connections: {
    inputs: string[];
    outputs: string[];
  };
  metadata?: {
    version: string;
    author?: string;
    description?: string;
    tags?: string[];
    created: Date;
    modified: Date;
    locked?: boolean;
    collapsed?: boolean;
  };
  validation?: {
    errors: ValidationError[];
    warnings: ValidationWarning[];
    lastValidated: Date;
  };
}

// Add a specific type for Entry block direction
export type EntryDirection = 'long' | 'short';

/** S91 — a block is "disabled" (excluded from evaluation, Pine + Python
 *  conversion, and validation) when data.disabled is true. Mirrors data.locked
 *  (both are UI-state flags living in BlockData). Every evaluator strips
 *  disabled blocks with `blocks.filter(b => !isBlockDisabled(b))`; downstream
 *  references to a now-absent block resolve to the logic-gate identity
 *  (engine drops the input; converters emit the true/false identity — S45/S85),
 *  so disabling a filter on an AND gate makes it neutral. */
export function isBlockDisabled(b: Block): boolean {
  return !!b.data?.disabled;
}

/** S92 — the EFFECTIVE disabled set: explicitly-disabled blocks PLUS, to a
 *  fixpoint, any NON-gate block that reads from a disabled block (it can no
 *  longer compute, so it's effectively disabled too). Logic gates are exempt —
 *  they drop missing inputs and evaluate the rest, so disabling a filter that
 *  feeds an AND gate cleanly removes that ONE branch instead of forcing the
 *  branch's tail expression to evaluate false (which would make the gate never
 *  fire). This is what makes "disable any block in a filter chain" behave as
 *  "remove that filter". Every evaluator filters on this set, not the raw flag. */
export function effectiveDisabledIds(blocks: Block[]): Set<string> {
  const disabled = new Set<string>();
  for (const b of blocks) if (b.data?.disabled) disabled.add(b.id);
  if (disabled.size === 0) return disabled;
  const refsOf = (b: Block): string[] => {
    const out: string[] = [...(b.connections?.inputs ?? [])];
    const d = b.data as Record<string, unknown> | undefined;
    for (const k of [
      'leftInputBlockId', 'rightInputBlockId',
      'rightLowInputBlockId', 'rightHighInputBlockId',
      'inputBlockId', 'sourceBlockId',
    ]) {
      const v = d?.[k];
      if (typeof v === 'string' && v) out.push(v);
    }
    return out;
  };
  let changed = true;
  while (changed) {
    changed = false;
    for (const b of blocks) {
      if (disabled.has(b.id) || b.type === BlockType.LOGIC_GATE) continue;
      const refs = refsOf(b);
      if (refs.length > 0 && refs.some((id) => disabled.has(id))) {
        disabled.add(b.id);
        changed = true;
      }
    }
  }
  return disabled;
}

// Update BlockData interface to use the specific type
export interface BlockData {
  name: string;
  description?: string;
  locked?: boolean;
  // S91 — Disable toggle. When true the block stays on the canvas (dimmed)
  // but is EXCLUDED from evaluation, conversion, and validation — filtered out
  // at each evaluator via isBlockDisabled(). Non-destructive ablation: toggle a
  // filter off, re-run the backtest, toggle it back on if it didn't help.
  disabled?: boolean;

  // Enhanced Expression fields per TSL spec
  leftSource?: ExpressionSourceType;
  leftValue?: number;
  leftInputBlockId?: string;
  leftOutputPort?: string;
  leftPriceSource?: string;
  leftPriceDataBlockId?: string;

  rightSource?: ExpressionSourceType;
  rightValue?: number;
  rightInputBlockId?: string;
  rightOutputPort?: string;
  rightPriceSource?: string;
  rightPriceDataBlockId?: string;

  // TSL v1.1.1 A.7: structured range operand for inside_range / outside_range.
  // Paired with rightSource = RANGE_STATIC (static low/high) or RANGE_INPUT
  // (connected blocks supply low/high series). Other operators reject these
  // sources via V-6f. Bounds default to inclusive on both sides.
  rightRange?: { low: number; high: number };       // For RANGE_STATIC
  rightLowInputBlockId?: string;                     // For RANGE_INPUT
  rightLowOutputPort?: string;
  rightHighInputBlockId?: string;
  rightHighOutputPort?: string;
  rangeBoundsInclusive?: { low: boolean; high: boolean }; // Default: { low: true, high: true }

  operator?: ExpressionOperator;
  persistence?: ExpressionPersistence;
  invert?: boolean;

  // Strategy Config specific (TSL compliant)
  title?: string;
  shortTitle?: string;
  overlay?: boolean;
  pyramiding?: number;
  maxPositions?: number;
  default_qty_type?: QuantityType;
  default_qty_value?: number;
  position_sizing_method?: 'fixed' | 'risk_based' | 'percent_equity' | 'kelly_criterion' | 'optimal_f' | 'volatility_adjusted';
  risk_per_trade?: number;
  equity_percent?: number;
  min_position_size?: number;
  max_position_size?: number;
  initial_capital?: number;
  currency?: string;
  max_drawdown?: number;
  max_risk_per_trade?: number;
  /**
   * Strategy-level "max trades per day" gate — mirrors Pine's
   *   var int tradesToday = 0
   *   if ta.change(time("D")) tradesToday := 0
   *   canTrade = (not oneTradePerDay or tradesToday < 1)
   * Enforced by the engine independently of any prop-firm preset, so
   * the rule travels with the strategy itself. When BOTH this and a
   * propFirm.maxTradesPerDay are set, the more restrictive wins
   * (either gate can fire). 1 = one trade per UTC day.
   */
  maxTradesPerDay?: number;
  slippage?: number;
  commission_type?: CommissionType;
  commission_value?: number;
  calc_on_order_fills?: boolean;
  calc_on_every_tick?: boolean;
  process_orders_on_close?: boolean;
  precision?: number;
  platform?: TradingPlatform;
  assetClass?: AssetClass;
  timeframe?: TimeFrame;

  // Price Data specific (TSL compliant)
  // S4f (2026-06-02) — `symbol?: string` removed. Engine never read it;
  // backtest + deployment both use the run-time selected symbol. See
  // docs/BLOCK_SETTINGS_AUDIT.md S4f section.
  source?: 'open' | 'high' | 'low' | 'close' | 'hl2' | 'hlc3' | 'ohlc4';
  // S4a (2026-06-02) — `lookback?: number` removed. Engine always
  // materializes the full backtest range; the per-block lookback knob
  // had no effect. See docs/BLOCK_SETTINGS_AUDIT.md.
  // TSL v1.1.1 A.8: integer number of BARS at THIS block's timeframe.
  // Negative = read N bars back (most common — shift=-1 compares to the
  // previous bar's close). Positive = forward look-ahead, only valid in
  // historical replay; suppressed at realtime edge. V-4c rejects non-integers.
  shift?: number;

  // Indicator specific (applies to all technical indicator blocks)
  indicator?: string;
  period?: number;
  smoothing?: number;
  multiplier?: number;

  // Momentum & Oscillator specific (merged block per TSL)
  overbought?: number;
  oversold?: number;
  // S5a (2026-06-02) — opt-in auto-fire on OB/OS extremes.
  // When `true` AND both `overbought` and `oversold` are finite + non-zero,
  // the momentum block's signals[] series fires at every bar where the
  // value is OUTSIDE the [oversold, overbought] band:
  //   signals[i] = (value < oversold) || (value > overbought)
  // The default (false / undefined) preserves the legacy behaviour:
  // signals[] is all-false and downstream consumers use Expression blocks
  // to compare the value series against literal thresholds. Existing
  // strategies are unchanged. See docs/BLOCK_SETTINGS_AUDIT.md S5a.
  emitExtremeSignals?: boolean;
  // S5b (2026-06-02) — opt-in auto-fire on trend threshold.
  // Companion flag for the `trend` block (ADX/DMI). When `true` AND
  // `threshold` is a finite positive number, evalTrend's signals[]
  // fires at every bar where the value EXCEEDS the threshold:
  //   signals[i] = value > threshold
  // ADX > 25 is the canonical "trending market" gate; this opt-in
  // surface lets the trend block emit that signal directly without
  // requiring a separate Expression block. Default off → all-false
  // signals (legacy behavior). See docs/BLOCK_SETTINGS_AUDIT.md S5b.
  emitThresholdSignals?: boolean;
  // S5c (2026-06-02) — band selector for banded volatility indicators
  // (Bollinger Bands, Keltner Channels, Donchian Channels). Closes the
  // A1 deferred work from the Bollinger/Donchian saga (A2 used CUSTOM
  // blocks as a workaround).
  //
  // Pre-S5c: every banded volatility indicator's values[] series was
  // hard-coded to the MIDDLE band, so close-vs-lower-band and
  // close-vs-upper-band expressions had to walk through a CUSTOM block
  // that re-implemented the indicator just to expose the desired band.
  //
  // Post-S5c: when `band: 'upper' | 'lower'` is set on a `bb`/'keltner'/
  // 'donchian' volatility block, evalVolatility / volatilityIndicator
  // returns THAT band as the block's values[]. Default `'middle'` (or
  // missing) preserves the legacy behaviour — every existing strategy
  // is unchanged.
  //
  // The selector is IGNORED for non-banded indicators (atr, chaikinVol,
  // any default-arm value) so a stray `band: 'upper'` on an ATR block
  // does no harm.
  band?: 'middle' | 'upper' | 'lower';
  kPeriod?: number;
  dPeriod?: number;
  kSmooth?: number;
  smoothK?: number;
  period1?: number;
  period2?: number;
  period3?: number;

  // MACD specific
  fastPeriod?: number;
  slowPeriod?: number;
  signalPeriod?: number;

  // Bollinger Bands specific
  stdDev?: number;

  // ATR specific
  atrMethod?: string;

  // ADX specific
  threshold?: number;

  // Custom Block specific (unified per TSL spec)
  // TSL v1.2-pre §X — strategy-logic custom-block sub-types. Same shape as the
  // per-bar custom blocks (`code` + `parameters`) but each is evaluated PER-
  // TRADE (synchronous, returns a single number) instead of pre-computed
  // per-bar by the worker. Engine dispatches when an entry references one:
  //   qty_calculator  → entry.qtyCalculatorBlockId → returns qty
  //   sl_calculator   → entry.slCalculatorBlockId  → returns SL price
  //   tp_calculator   → entry.tpCalculatorBlockId  → returns TP price
  // Each wins over its corresponding hardcoded engine branch (calcQty,
  // resolveStopLossPrice's stopType switch, resolveTakeProfitPrice's tpType
  // switch). Authored Pine logic (calcQty, sl =..., tpOnly =...) now lives
  // in the TSL graph instead of as hardcoded engine cases.
  blockType?: 'indicator' | 'pattern' | 'analysis' | 'filter' | 'signal_generator'
            | 'qty_calculator' | 'sl_calculator' | 'tp_calculator'
            // Slice B.1 — risk_rule custom blocks. Carry per-trade /
            // per-bar prop-firm risk enforcement (maxTradesPerDay,
            // future: maxDailyLoss/trailing DD/etc.) authored in the
            // graph instead of as hardcoded engine branches.
            | 'risk_rule';
  // When an ENTRY block references a `qty_calculator` custom block, the engine
  // evaluates that block's `computeQty(ctx, params, helpers)` to size the
  // trade. Wins over all other sizing fields on the entry (Pine semantic).
  qtyCalculatorBlockId?: string;
  // Same wiring for SL / TP calculators. computeSl / computeTp return the
  // stop / target PRICE in instrument units, called once at entry. Win over
  // the engine's stopType / tpType branches in src/engine/sizing.ts.
  slCalculatorBlockId?: string;
  tpCalculatorBlockId?: string;
  code?: string;
  parameters?: Record<string, unknown>;
  // Custom Block Library provenance — set when a CUSTOM block is inserted from the library. The code is
  // snapshot-copied (immutable in the strategy); these record which library block/version it came from
  // so the UI can flag "this library block was delisted" and AI/optimizer runs are reproducible.
  libraryBlockId?: string;
  libraryVersion?: number;
  // Safe TSL Block — declarative AST for custom blocks (no host code). When present the controlled
  // interpreter runs it (src/engine/tslBlock/interpret.ts). `tslText` is the canonical human-readable
  // source (printBlock(tslAst)) the editor / Strategy Script surfaces display and re-parse.
  tslAst?: import('../engine/tslBlock/ast').TslBlock;
  tslText?: string;
  // Phase 2 provenance — where the library block came from (curated built-in vs. a public user block)
  // and who authored it, so the UI can label source/author and a disabled/delisted source can warn.
  librarySource?: 'builtin' | 'user';
  libraryAuthor?: string;
  libraryPublic?: boolean;
  // S4d (2026-06-02) — `outputTypes?: Array<...>` removed. The engine
  // infers the custom-block subtype dispatch from `blockType` alone
  // (src/engine/customBlockBacktestWorker.ts:71 maps blockType →
  // calculate/detectPattern/analyze). The outputTypes UI checkbox
  // group + 4 V-14 validators + 8 template write sites + the
  // StrategyBlock summary were all dead surface. See
  // docs/BLOCK_SETTINGS_AUDIT.md S4d section.
  //
  // NOTE on `executionTier`: per audit + user direction it stays as
  // a product-decision token even though the customBlockRuntime's
  // computeTimeoutMs() applies a single data-size-scaled timeout
  // regardless of the value. Wiring it to a real two-tier timeout
  // split is S5 territory (adds capability, not S4 dead-field cleanup).
  executionTier?: 'standard' | 'heavy';
  // P2 (2026-06-10) — Strategy Script API version stamp (docs/
  // STRATEGY_SCRIPT_API.md). Absent = 1. Bumped only on breaking changes
  // to the code contracts; the engine keeps a dispatch path per major.
  scriptApiVersion?: number;

  // Pattern Recognition specific (2 separate block types per TSL)
  patternType?: string;
  chartPatterns?: string[]; // For CHART_PATTERNS block
  candlestickPatterns?: string[]; // For CANDLESTICK_PATTERNS block
  sensitivity?: 'strict' | 'medium' | 'loose';
  confirmationBars?: number;
  minPatternSize?: number;

  // Logic gate specific
  type?: string;
  timeout?: Duration;
  // S4a (2026-06-02) — `fuzzyLogic?: boolean` removed. Engine's
  // evalLogicGate (src/engine/graph.ts:532) only dispatches on
  // `type` + `invert`; the fuzzy-logic toggle was dead UI surface.
  // The companion `threshold` field stays in the BlockData union
  // because it's a real, in-use setting for trend / momentum
  // blocks (their threshold is currently MISSING engine wiring —
  // S5 work, NOT to be touched in S4). See audit doc.

  // Entry specific (TSL compliant - now terminal block)
  entryType?: 'market' | 'limit' | 'stop' | 'stop_limit';
  direction?: EntryDirection; // Use specific type instead of string
  orderType?: 'simple' | 'bracket' | 'trailing';
  bracketStopLoss?: number;
  bracketTakeProfit?: number;
  bracketStopType?: 'points' | 'price' | 'percentage';
  bracketTPType?: 'points' | 'price' | 'percentage';
  trailingAmount?: number;
  trailingActivation?: number;
  useStrategyDefaults?: boolean;
  quantity_type?: QuantityType;
  quantity_value?: number;
  kelly_lookback?: number;
  kelly_fraction?: number;
  volatility_lookback?: number;
  base_position_size?: number;
  alertOnEntry?: boolean;
  // S4b (2026-06-02) — entry scale-in fields removed.
  //   allowScaling?: boolean | maxScaleIns?: number | scaleInSize?: number
  // The engine's ClientSideBacktestEngine never iterates these; pyramiding
  // itself works via cfg.pyramiding (strategy_config). The validation
  // rules that linted these fields (V-12c, V-13d) were removed in the
  // same slice. See docs/BLOCK_SETTINGS_AUDIT.md.

  // Exit specific (TSL compliant - now terminal block)
  exitType?: 'market' | 'limit';
  expirationTime?: number;
  expirationUnit?: 'minutes' | 'hours' | 'days';
  exitSize?: 'full' | 'partial' | 'percentage';
  exitPercentage?: number;
  // S4b (2026-06-02) — exit scale-out fields removed.
  //   allowScaleOut?: boolean | scaleOutLevels?: Array<{trigger, percentage}>
  // The engine never iterates the scaleOutLevels array; ladder-exits were
  // never implemented. Validation rules V-12d + V-26c removed in the same
  // slice. See docs/BLOCK_SETTINGS_AUDIT.md.
  alertOnExit?: boolean;
  // TSL v1.1.1 B.6: position targeting on Exit / StopLoss / TakeProfit.
  // When multiple positions exist (pyramiding > 1), these fields disambiguate
  // which positions the exit affects. Resolution order:
  //   1. closeAll:true (Exit only) — every open position regardless of direction.
  //   2. targetEntryBlockId — positions opened by that Entry block only.
  //   3. Subgraph linkage — exactly one Entry in the connected component.
  //   4. targetDirection — long/short bucket; halt V-26 if no direction inferable.
  // Partial exit semantics: when multiple positions match, exitPercentage
  // applies PER POSITION, not in aggregate. Use closeAll:true to apply
  // percentage to total exposure across all positions.
  targetEntryBlockId?: string;
  targetDirection?: 'long' | 'short' | 'all';
  closeAll?: boolean;

  // Stop Loss specific (TSL compliant - now terminal block)
  // S4c (2026-06-02) — `executionMethod?: 'condition_based' | 'indicator_based'`
  // removed. The engine never dispatched on it; SL/TP semantics are
  // determined entirely by `stopType` / `tpType` + their subtype fields.
  // The companion V-9a/V-9b validators that gated on this field have been
  // refactored / removed in the same slice. See docs/BLOCK_SETTINGS_AUDIT.md.
  stopType?: StopType;
  stopValue?: number;
  // S4c (2026-06-02) — `overrideConditions?: boolean` removed. Vestigial
  // UI flag with no engine consumer; the engine treats all stops as
  // condition-based by design. See audit doc.
  activationPrice?: number;
  trailingStep?: number;
  // T-1m (2026-06-10) — volatility-adaptive trailing. How the per-bar trail
  // distance is derived for stopType 'trailing' / 'trailing_limit':
  //   'points'     (default) → trailingStep, a fixed price distance
  //   'atr'        → ATR(14) × atrMultiplier (adapts to volatility — the
  //                  right choice on 1m/5m where fixed points are scale-
  //                  dependent and go stale)
  //   'percentage' → trailingPercent % of the trailing extreme price
  // The engine consumes this in maybeUpdateTrailingStop AND uses the same
  // distance for the INITIAL stop at entry (resolveStopLossPrice).
  trailingType?: 'points' | 'atr' | 'percentage';
  trailingPercent?: number;
  timeDuration?: number;
  timeUnit?: 'minutes' | 'hours' | 'days';
  atrMultiplier?: number;
  volatilityMultiplier?: number;
  volatilityPeriod?: number;
  limitOffset?: number;
  // TSL v1.1.1 A.3: StopLoss breakeven is now TWO disambiguated fields.
  // The PRIMARY breakeven (when stopType: 'breakeven') uses breakevenTrigger
  // + breakevenOffset. The MIGRATION OVERLAY (on top of any other stopType,
  // when moveToBreakeven: true) uses moveToBreakevenTrigger + moveToBreakevenOffset.
  // Per V-22a/V-22b/V-22c the two modes are mutually exclusive; using
  // them together is a validation error.
  breakevenTrigger?: number;             // Profit (points) — required when stopType is 'breakeven'.
  breakevenOffset?: number;              // Points beyond entry (default: 0).
  moveToBreakeven?: boolean;
  moveToBreakevenTrigger?: number;       // Profit (points) at which the configured stop migrates.
  moveToBreakevenOffset?: number;        // Default: 0.
  exitMode?: 'full' | 'partial' | 'scaled';
  partialQuantity?: number;
  // S4c (2026-06-02) — `scaledLevels?: Array<{trigger, quantity}>` removed.
  // The engine never iterated it; ladder-exits for SL/TP were never
  // implemented. The `exitMode: 'scaled'` enum value remains in the
  // type union — `detectUnimplementedExits` in src/engine/strategyGuards.ts
  // already surfaces it as unimplemented. A future S5 slice can wire
  // scaled exits + reinstate this field if the capability ships.
  alertOnStopLoss?: boolean;

  // Take Profit specific (TSL compliant - now terminal block)
  tpType?: TakeProfitType;
  tpValue?: number;
  riskRewardRatio?: number;
  trailingOffset?: number;
  activationLevel?: number;
  // TSL v1.1.1 A.3: TakeProfit's prior `breakevenTrigger` is renamed to
  // `moveToBreakevenTrigger` for symmetry with the StopLoss naming. The
  // old field name is still read for back-compat (see strategyValidation).
  // TSL v1.1.1 B.5: when tpType === 'risk_reward', the engine must locate a
  // matching Stop Loss to compute R. linkedStopLossBlockId disambiguates
  // when multiple SL candidates exist; otherwise the resolution algorithm
  // (V-25) walks the connected component.
  linkedStopLossBlockId?: string;
  // S4c (2026-06-02) — `targets?: Array<{level, quantity, type}>` removed.
  // Take-profit ladder editor in BlockEditor.tsx wrote into this field
  // when tpType === SCALED, but the engine never read it. The UI editor
  // has been removed in the same slice. See audit doc.
  alertOnTakeProfit?: boolean;
  // S4c (2026-06-02) — `allowModification?: boolean` removed. Vestigial
  // flag with no engine consumer; the companion V-13b validator that
  // gated on it has been removed in the same slice. See audit doc.

  // Filter specific
  filterType?: string;
  conditions?: Record<string, unknown>;

  // Time filter specific
  sessions?: Record<string, boolean>;
  tradingDays?: Record<string, boolean>;
  startTime?: string;
  endTime?: string;
  timezone?: string;
  sessionFilter?: {
    sessions: string[];
    timezone: string;
  };
  dayFilter?: {
    allowedDays: string[];
  };
  timeRangeFilter?: {
    startTime: string;
    endTime: string;
    timezone: string;
  };

  // Volatility indicator specific
  basis?: string;
  maType?: string;
  atrPeriod?: number;
  showMiddle?: boolean;
  rocPeriod?: number;

  // Volume indicator specific
  // S4a (2026-06-02) — `showMA?: boolean` and `showBands?: boolean`
  // removed. Both were dead UI/charting fields with zero engine
  // consumers (graph.ts:evalVolume ignores them); the only writers
  // were the AI-Optimize structuralFallback which has been cleaned up
  // in the same slice. See docs/BLOCK_SETTINGS_AUDIT.md.
  maPeriod?: number;
  session?: string;
  anchor?: string;
  stdDev1?: number;
  stdDev2?: number;
  scale?: number;

  // Trend indicator specific
  accelerationFactor?: number;
  maxAcceleration?: number;
  tenkanPeriod?: number;
  kijunPeriod?: number;
  senkouBPeriod?: number;
  displacement?: number;
  showCloud?: boolean;
  showChikou?: boolean;
  showOscillator?: boolean;

  // Moving Average specific
  offset?: number;
  timeOffset?: number;
  sigma?: number;
  start?: number;
  increment?: number;
  maximum?: number;

  // Generic properties for extensibility
  [key: string]: unknown;
}

export const getDefaultExpressionData = (): Partial<BlockData> => ({
  name: 'Expression',
  leftSource: ExpressionSourceType.STATIC,
  leftValue: 0,
  rightSource: ExpressionSourceType.STATIC,
  rightValue: 0,
  operator: ExpressionOperator.GREATER_THAN,
  invert: false,
});

export interface ExpressionValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface ValidationError {
  id: string;
  type: 'critical' | 'error' | 'warning' | 'info';
  message: string;
  blockId?: string;
  field?: string;
  suggestion?: string;
  code: string;
}

export interface ValidationWarning {
  id: string;
  type: 'performance' | 'risk' | 'best_practice' | 'compatibility';
  message: string;
  blockId?: string;
  field?: string;
  suggestion?: string;
  severity: 'low' | 'medium' | 'high';
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  performance: {
    complexity: number;
    estimatedMemoryUsage: number;
    estimatedExecutionTime: number;
  };
  suggestions: string[];
}

export interface Strategy {
  id: string;
  name: string;
  description: string;
  blocks: Block[];
  platform: TradingPlatform;
  assetClass: AssetClass;
  version: string;
  tags: string[];
  author?: {
    id: string;
    name: string;
    email?: string;
  };
  created: Date;
  updated: Date;
  isPublic: boolean;
  stats?: {
    downloads: number;
    rating: number;
    reviews: number;
    backtestResults?: BacktestResults;
  };
  metadata?: {
    complexity: number;
    riskLevel: 'low' | 'medium' | 'high';
    timeframe: TimeFrame[];
    marketConditions: string[];
    experience: 'beginner' | 'intermediate' | 'advanced';
  };
}

export interface StrategyTemplate extends Strategy {
  category:
    | 'trend'
    | 'momentum'
    | 'breakout'
    | 'mean_reversion'
    | 'pattern'
    | 'arbitrage'
    | 'scalping'
    | 'hybrid'
    | 'other';
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  timeframes: string[];
  marketConditions: string[];
  requiredData: string[];
  defaultParameters: Record<string, unknown>;
  // Lifecycle (migration 0074). 'retired' templates redirect to their migrated successor on load and are
  // hidden from normal discovery; the rows are preserved for audit. Absent ⇒ 'active'.
  lifecycleStatus?: 'active' | 'retired';
  migratedToTemplateId?: string | null;
  retiredReason?: string | null;
}

export interface BacktestResults {
  totalReturn: number;
  annualizedReturn: number;
  sharpeRatio: number;
  maxDrawdown: number;
  winRate: number;
  profitFactor: number;
  totalTrades: number;
  avgTradeDuration: Duration;
  avgWin: number;
  avgLoss: number;
  largestWin: number;
  largestLoss: number;
  consecutiveWins: number;
  consecutiveLosses: number;
  equityCurve: Point[];
  trades: Trade[];
  monthlyReturns: Array<{ month: string; return: number }>;
  metrics: BacktestMetrics;
}

export interface Trade {
  id: string;
  symbol: string;
  side: 'long' | 'short';
  entryTime: Date;
  exitTime: Date;
  entryPrice: number;
  exitPrice: number;
  quantity: number;
  size: number;
  equityBefore?: number; // account equity just before this trade was opened
  pnl: number;
  pnlPercentage: number;
  commission: number;
  duration: Duration;
  entryReason: string;
  exitReason: string;
  maxFavorableExcursion: number;
  maxAdverseExcursion: number;
  // Engine-emitted per-trade excursions in PRICE units (per unit of size),
  // always non-negative. mfe = best favorable move from entry; mae = worst
  // adverse move. $ value = mfe/mae × size. (The engine sets these names;
  // maxFavorable/AdverseExcursion above are the legacy aliases.)
  mfe?: number;
  mae?: number;
  // Optional additional fields that might be useful
  spread?: number;
  slippage?: number;
  swap?: number;
  tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface MarketData {
  symbol: string;
  timeframe: TimeFrame;
  candles: Candle[];
  lastUpdate: Date;
  source: string;
}

export interface Tick {
  symbol: string;
  bid: number;
  ask: number;
  last: number;
  volume: number;
  timestamp: Date;
}

export interface Connection {
  id: string;
  sourceBlockId: string;
  targetBlockId: string;
  sourcePort: string;
  targetPort: string;
  dataType: string;
  metadata?: {
    label?: string;
    color?: string;
    weight?: number;
  };
}

export interface Port {
  id: string;
  name: string;
  type: 'input' | 'output';
  dataType: string;
  required: boolean;
  multiple: boolean;
  description?: string;
}

export interface CollaborationUser {
  id: string;
  name: string;
  color: string;
  cursor?: { x: number; y: number };
  selection?: string[];
  lastActivity: Date;
}

export interface StrategyShare {
  id: string;
  strategyId: string;
  sharedWith: string;
  permissions: {
    canView: boolean;
    canEdit: boolean;
    canCopy: boolean;
    canShare: boolean;
  };
  sharedBy: string;
  sharedAt: Date;
  expiresAt?: Date;
}

export interface Alert {
  id: string;
  type: 'risk' | 'performance' | 'execution' | 'system';
  level: 'info' | 'warning' | 'error' | 'critical';
  message: string;
  timestamp: Date;
  acknowledged: boolean;
  source: string;
  metadata?: Record<string, unknown>;
}

export interface RiskMetrics {
  currentDrawdown: number;
  maxDrawdown: number;
  dailyVaR: number;
  portfolioRisk: number;
  concentrationRisk: number;
  correlationRisk: number;
  leverageRatio: number;
}

// New types per TSL spec
export enum OrderAction {
  BRACKET = 'bracket',
  TRAILING = 'trailing',
  SCALE_IN = 'scale_in',
  SCALE_OUT = 'scale_out',
  PARTIAL_EXIT = 'partial_exit',
  MODIFY = 'modify',
}

export interface StopEvent {
  orderId: string;
  positionId: string;
  triggerPrice: number;
  executionPrice: number;
  timestamp: Date;
  reason: string;
}

export interface TakeProfitEvent {
  orderId: string;
  positionId: string;
  targetPrice: number;
  executionPrice: number;
  timestamp: Date;
  reason: string;
}

export interface ClosedPosition {
  id: string;
  symbol: string;
  side: 'long' | 'short';
  entryPrice: number;
  exitPrice: number;
  size: number;
  pnl: number;
  commission: number;
  entryTime: Date;
  exitTime: Date;
  exitReason: string;
}

// Export utility types
export type BlockTypeMap = {
  [K in BlockType]: Block & { type: K };
};

export type RequiredBlockData<T extends BlockType> = Required<
  Pick<
    BlockData,
    T extends BlockType.STRATEGY_CONFIG ? 'title' | 'initial_capital' : never
  >
>;

export const createTrade = (baseData: Partial<Trade>): Trade => {
  const now = new Date();
  return {
    id: baseData.id || `trade_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    symbol: baseData.symbol || 'UNKNOWN',
    side: baseData.side || 'long',
    entryTime: baseData.entryTime || now,
    exitTime: baseData.exitTime || now,
    entryPrice: baseData.entryPrice || 0,
    exitPrice: baseData.exitPrice || 0,
    quantity: baseData.quantity || 0,
    size: baseData.size || 0,
    pnl: baseData.pnl || 0,
    pnlPercentage: baseData.pnlPercentage || 0,
    commission: baseData.commission || 0,
    duration: baseData.duration || { value: 0, unit: 'minutes' },
    entryReason: baseData.entryReason || 'Unknown',
    exitReason: baseData.exitReason || 'Unknown',
    maxFavorableExcursion: baseData.maxFavorableExcursion || 0,
    maxAdverseExcursion: baseData.maxAdverseExcursion || 0,
    spread: baseData.spread,
    slippage: baseData.slippage,
    swap: baseData.swap,
    tags: baseData.tags || [],
    metadata: baseData.metadata || {},
  };
};

export const validateTrade = (
  trade: Trade
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!trade.symbol || trade.symbol.trim() === '') {
    errors.push('Trade must have a valid symbol');
  }

  if (!['long', 'short'].includes(trade.side)) {
    errors.push('Trade side must be either "long" or "short"');
  }

  if (trade.entryPrice <= 0) {
    errors.push('Entry price must be greater than 0');
  }

  if (trade.exitPrice <= 0) {
    errors.push('Exit price must be greater than 0');
  }

  if (trade.quantity <= 0) {
    errors.push('Quantity must be greater than 0');
  }

  if (trade.entryTime >= trade.exitTime) {
    errors.push('Entry time must be before exit time');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Block dimensions per TSL spec
export const DEFAULT_BLOCK_SIZE = {
  width: 200,
  height: 120,
  minWidth: 150,
  minHeight: 80,
  maxWidth: 400,
  maxHeight: 300,
  strategyConfigWidth: 300, // Strategy config is wider per TSL spec
} as const;

// Enhanced types for TradingView-like interface
export interface TradingState {
  symbol: string;
  timeframe: string;
  marketData: MarketDataPoint[];
  isDataLoaded: boolean;
  // Honest-positioning pass: 'live' removed because no live-execution
  // engine exists yet. Re-add when server-side strategy execution ships
  // (see docs/BETA_LAUNCH_CHECKLIST.md "What you're NOT shipping").
  currentMode: 'backtest' | 'replay';
  replayPosition: number;
  isReplayPlaying: boolean;
  replaySpeed: number;
  startDate: string;
  endDate: string;
  initialBalance: number;
}

export interface MarketDataPoint {
  timestamp: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  symbol?: string;
}

export interface ChartDataPoint {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface VolumeDataPoint {
  time: number;
  value: number;
  color: string;
}

export interface ReplayState {
  position: number;
  isPlaying: boolean;
  speed: number;
}

export interface LiveTradingState {
  isConnected: boolean;
  currentPosition: Position | null;
  openOrders: Order[];
  todayPnL: number;
  lastUpdate: Date;
}

export interface BacktestMetrics {
  netProfit: number;
  winRate: number;
  maxDrawdown: number;
  totalTrades: number;
  averageTrade: number;
  averageWin: number;
  averageLoss: number;
  profitFactor: number;
  sharpeRatio: number;
  expectancy: number;
  riskRewardRatio: number;
  breakEvenTrades: number;
  largestWin: number;
  largestLoss: number;
  averageHoldingTime: number;
  profitableMonths: number;
  maxConsecutiveWins: number;
  maxConsecutiveLosses: number;
}

export interface BacktestResults {
  trades: Trade[];
  pnl: number;
  equityCurve: Point[];
  metrics: BacktestMetrics;
  riskAnalysis: {
    valueAtRisk: number;
    maxDrawdown: number;
    riskOfRuin: number;
    kellyPercentage: number;
    marginUtilization: number;
    riskPerTrade: number;
    riskRewardRatio: number;
    correlationMatrix: number[][];
    stressTest: {
      marketCrash: { maxDrawdown: number; valueAtRisk: number; riskOfRuin: number };
      volatilitySpike: { maxDrawdown: number; valueAtRisk: number; kellyPercentage: number };
      correlationBreakdown: { correlationMatrix: number[][]; valueAtRisk: number };
      liquidityCrisis: { marginUtilization: number; riskPerTrade: number };
    };
  };
  dataProcessed: number;
  tradesGenerated: number;
  unlimited?: boolean;
}

export interface AlertCondition {
  operator: '>' | '<' | '>=' | '<=' | '==' | '!=';
  value: number;
  field?: string;
  blockId?: string;
  indicator?: string;
}

export interface AlertTriggerSettings {
  once: boolean;
  frequency: 'once' | 'every_bar' | 'every_minute' | 'every_hour';
  cooldown?: number;
}

export interface AlertNotification {
  email?: string;
  webhook?: string;
  inApp: boolean;
  pushNotification?: boolean;
}

export interface TradingAlert {
  id: string;
  name: string;
  description?: string;
  type: 'price' | 'indicator' | 'strategy' | 'time';
  condition: AlertCondition;
  trigger: AlertTriggerSettings;
  notification: AlertNotification;
  isActive: boolean;
  createdAt: Date;
  lastTriggered?: Date;
  triggerCount: number;
  metadata?: Record<string, unknown>;
}

export interface AlertTrigger {
  id: string;
  alertId: string;
  timestamp: Date;
  value: number;
  message: string;
  acknowledged: boolean;
  metadata?: Record<string, unknown>;
}

// Component prop types
export interface UnifiedTradingPanelProps {
  blocks: Block[];
  tradingState: TradingState;
  onTradingStateChange: (state: Partial<TradingState>) => void;
  onExecutionStart: (mode: 'backtest' | 'replay' | 'live') => void;
  onExecutionStop: () => void;
}

export interface ChartContainerProps {
  data: MarketDataPoint[];
  isDataLoaded: boolean;
  mode: 'backtest' | 'replay' | 'live';
  replayPosition?: number;
  onReplayPositionChange?: (position: number) => void;
  height?: number;
}

export interface SymbolTimeframeSelectorProps {
  symbol: string;
  timeframe: string;
  onSymbolChange: (symbol: string) => void;
  onTimeframeChange: (timeframe: string) => void;
  onDataLoad?: (data: MarketDataPoint[]) => void;
}

export interface TradingModeSelectorProps {
  currentMode: 'backtest' | 'replay';
  onModeChange: (mode: 'backtest' | 'replay' | 'live') => void;
  isExecuting: boolean;
}

export interface TradingControlsProps {
  mode: 'backtest' | 'replay' | 'live';
  blocks: Block[];
  isExecuting: boolean;
  onExecutionStart: (mode: 'backtest' | 'replay' | 'live') => void;
  onExecutionStop: () => void;
  replayState: ReplayState;
  onReplayStateChange: (replayState: Partial<ReplayState>) => void;
  initialBalance: number;
  onInitialBalanceChange: (balance: number) => void;
}

export interface AlertManagerProps {
  alerts: TradingAlert[];
  onAlertCreate: (alert: Omit<TradingAlert, 'id' | 'createdAt' | 'triggerCount'>) => void;
  onAlertUpdate: (id: string, alert: Partial<TradingAlert>) => void;
  onAlertDelete: (id: string) => void;
}

export interface AlertListProps {
  alerts: TradingAlert[];
  onAlertSelect: (alert: TradingAlert) => void;
  onAlertDelete: (id: string) => void;
  selectedAlertId?: string;
}

export interface AlertFormProps {
  alert?: TradingAlert;
  onSubmit: (alert: Omit<TradingAlert, 'id' | 'createdAt' | 'triggerCount'>) => void;
  onCancel: () => void;
}

export interface BacktestResultsPanelProps {
  results: BacktestResults | null;
}

export interface LiveTradingPanelProps {
  liveState: LiveTradingState;
}
